import { BaseShader } from './base-shader';

export class CrtShader extends BaseShader {
  create() {
    const fragmentSource = `#version 300 es
precision mediump float;

// Input uniforms from Excalibur
uniform float u_time_ms;
uniform sampler2D u_graphic;
uniform vec2 u_graphic_resolution;

// Constants ported from original shader parameters
const bool overlay = false;
const float scanlines_opacity = 0.4;
const float scanlines_width = 0.25;
const float grille_opacity = 0.3;

const bool pixelate = false;

const bool roll = true;
const float roll_speed = -8.0;
const float roll_size = 15.0;
const float roll_variation = 1.8;
const float distort_intensity = 0.01;

const float noise_opacity = 0.1;
const float noise_speed = 5.0;

const float static_noise_intensity = 0.06;

const float aberration = 0.01;
const float brightness = 2.5;
const bool discolor = false;

const float warp_amount = 1.0;
const bool clip_warp = true;

const float vignette_intensity = 0.4;
const float vignette_opacity = 0.5;

// Input/output
in vec2 v_uv;
in vec2 v_screenuv;
out vec4 fragColor;

// Rest of the shader code remains exactly the same...
vec2 random(vec2 uv) {
    uv = vec2(
        dot(uv, vec2(127.1, 311.7)),
        dot(uv, vec2(269.5, 183.3))
    );
    return -1.0 + 2.0 * fract(sin(uv) * 43758.5453123);
}

float noise(vec2 uv) {
    vec2 uv_index = floor(uv);
    vec2 uv_fract = fract(uv);
    vec2 blur = smoothstep(0.0, 1.0, uv_fract);
    
    return mix(
        mix(
            dot(random(uv_index + vec2(0.0, 0.0)), uv_fract - vec2(0.0, 0.0)),
            dot(random(uv_index + vec2(1.0, 0.0)), uv_fract - vec2(1.0, 0.0)),
            blur.x
        ),
        mix(
            dot(random(uv_index + vec2(0.0, 1.0)), uv_fract - vec2(0.0, 1.0)),
            dot(random(uv_index + vec2(1.0, 1.0)), uv_fract - vec2(1.0, 1.0)),
            blur.x
        ),
        blur.y
    ) * 0.5 + 0.5;
}

vec2 warp(vec2 uv) {
    vec2 delta = uv - 0.5;
    float delta2 = dot(delta.xy, delta.xy);
    float delta4 = delta2 * delta2;
    float delta_offset = delta4 * warp_amount;
    return uv + delta * delta_offset;
}

float border(vec2 uv) {
    float radius = min(warp_amount, 0.08);
    radius = max(min(min(abs(radius * 2.0), abs(1.0)), abs(1.0)), 1e-5);
    vec2 abs_uv = abs(uv * 2.0 - 1.0) - vec2(1.0, 1.0) + radius;
    float dist = length(max(vec2(0.0), abs_uv)) / radius;
    float square = smoothstep(0.96, 1.0, dist);
    return clamp(1.0 - square, 0.0, 1.0);
}

float vignette(vec2 uv) {
    uv *= 1.0 - uv.xy;
    float vig = uv.x * uv.y * 15.0;
    return pow(vig, vignette_intensity * vignette_opacity);
}

void main() {
    vec2 uv = overlay ? warp(v_screenuv) : warp(v_uv);
    vec2 text_uv = uv;
    vec2 roll_uv = vec2(0.0);
    float time = roll ? u_time_ms / 1000.0 : 0.0;

    if (pixelate) {
        text_uv = ceil(uv * u_graphic_resolution) / u_graphic_resolution;
    }

    float roll_line = 0.0;
    if (roll || noise_opacity > 0.0) {
        roll_line = smoothstep(0.3, 0.9, sin(uv.y * roll_size - (time * roll_speed)));
        roll_line *= roll_line * smoothstep(0.3, 0.9, sin(uv.y * roll_size * roll_variation - (time * roll_speed * roll_variation)));
        roll_uv = vec2((roll_line * distort_intensity * (1.0 - v_uv.x)), 0.0);
    }

    vec4 text;
    if (roll) {
        text.r = texture(u_graphic, text_uv + roll_uv * 0.8 + vec2(aberration, 0.0) * 0.1).r;
        text.g = texture(u_graphic, text_uv + roll_uv * 1.2 - vec2(aberration, 0.0) * 0.1).g;
        text.b = texture(u_graphic, text_uv + roll_uv).b;
        text.a = 1.0;
    } else {
        text.r = texture(u_graphic, text_uv + vec2(aberration, 0.0) * 0.1).r;
        text.g = texture(u_graphic, text_uv - vec2(aberration, 0.0) * 0.1).g;
        text.b = texture(u_graphic, text_uv).b;
        text.a = 1.0;
    }

    float r = text.r;
    float g = text.g;
    float b = text.b;

    uv = warp(v_uv);

    if (grille_opacity > 0.0) {
        float g_r = smoothstep(0.85, 0.95, abs(sin(uv.x * (u_graphic_resolution.x * 3.14159265))));
        r = mix(r, r * g_r, grille_opacity);
        float g_g = smoothstep(0.85, 0.95, abs(sin(1.05 + uv.x * (u_graphic_resolution.x * 3.14159265))));
        g = mix(g, g * g_g, grille_opacity);
        float b_b = smoothstep(0.85, 0.95, abs(sin(2.1 + uv.x * (u_graphic_resolution.x * 3.14159265))));
        b = mix(b, b * b_b, grille_opacity);
    }

    text.r = clamp(r * brightness, 0.0, 1.0);
    text.g = clamp(g * brightness, 0.0, 1.0);
    text.b = clamp(b * brightness, 0.0, 1.0);

    float scanlines = 0.5;
    if (scanlines_opacity > 0.0) {
        scanlines = smoothstep(scanlines_width, scanlines_width + 0.5, abs(sin(uv.y * (u_graphic_resolution.y * 3.14159265))));
        text.rgb = mix(text.rgb, text.rgb * vec3(scanlines), scanlines_opacity);
    }

    if (noise_opacity > 0.0) {
        float noise = smoothstep(0.4, 0.5, noise(uv * vec2(2.0, 200.0) + vec2(10.0, (time * noise_speed))));
        roll_line *= noise * scanlines * clamp(random((ceil(uv * u_graphic_resolution) / u_graphic_resolution) + vec2(time * 0.8, 0.0)).x + 0.8, 0.0, 1.0);
        text.rgb = clamp(mix(text.rgb, text.rgb + roll_line, noise_opacity), vec3(0.0), vec3(1.0));
    }

    if (static_noise_intensity > 0.0) {
        text.rgb += clamp(random((ceil(uv * u_graphic_resolution) / u_graphic_resolution) + fract(time)).x, 0.0, 1.0) * static_noise_intensity;
    }

    text.rgb *= border(uv);
    text.rgb *= vignette(uv);

    if (clip_warp) {
        text.a = border(uv);
    }

    if (discolor) {
        vec3 greyscale = vec3(text.r + text.g + text.b) / 3.0;
        float saturation = 0.5;
        text.rgb = mix(text.rgb, greyscale, saturation);
        
        float contrast = 1.2;
        float midpoint = pow(0.5, 2.2);
        text.rgb = (text.rgb - vec3(midpoint)) * contrast + midpoint;
    }

    fragColor = text;
    fragColor.rgb = fragColor.rgb * fragColor.a; // Premultiply alpha for Excalibur
}`;

    return this.factory.createMaterial('crt', fragmentSource);
  }
}
